import { getCelebrityById, getAllCelebrities, getCelebritiesByTier } from '../mockData/celebrities';
import { getCompanyById, getAllCompanies, getCompaniesByCelebrity } from '../mockData/companies';

class MockCelebrityService {
  // Simulate API delay
  async delay(ms = 100) {
    await new Promise(resolve => setTimeout(resolve, ms));
  }

  // Get all celebrities with optional filtering
  async getAllCelebrities(tier = null) {
    await this.delay();
    
    if (tier) {
      return getCelebritiesByTier(tier);
    }
    return getAllCelebrities();
  }
  
  // Get celebrity by PitchBook ID
  async getCelebrityById(pb_id) {
    await this.delay();
    const celebrity = getCelebrityById(pb_id);
    
    if (!celebrity) {
      throw new Error(`Celebrity with ID ${pb_id} not found`);
    }
    
    return celebrity;
  }
  
  // Get celebrity details (full profile)
  async getCelebrityDetails(pb_id) {
    await this.delay();
    const celebrity = getCelebrityById(pb_id);
    
    if (!celebrity) {
      throw new Error(`Celebrity with ID ${pb_id} not found`);
    }
    
    // If celebrity doesn't have full data, generate realistic partial data
    if (!celebrity.has_full_data) {
      return this.generatePartialProfile(celebrity);
    }
    
    return celebrity;
  }
  
  // Get company details
  async getCompanyDetails(company_id) {
    await this.delay();
    const company = getCompanyById(company_id);
    
    if (!company) {
      // Generate realistic data for companies not in our mock data
      return this.generateCompanyProfile(company_id);
    }
    
    return company;
  }
  
  // Get all companies
  async getAllCompanies() {
    await this.delay();
    return getAllCompanies();
  }
  
  // Get companies by celebrity investor
  async getCompaniesByCelebrity(celebrity_name) {
    await this.delay();
    return getCompaniesByCelebrity(celebrity_name);
  }
  
  // Search celebrities
  async searchCelebrities(query) {
    await this.delay();
    const allCelebrities = getAllCelebrities();
    const searchLower = query.toLowerCase();
    
    return allCelebrities.filter(celebrity => 
      celebrity.investor_name.toLowerCase().includes(searchLower) ||
      celebrity.display_role.toLowerCase().includes(searchLower) ||
      celebrity.preferred_industries.some(ind => ind.toLowerCase().includes(searchLower))
    );
  }
  
  // Get celebrity investments
  async getCelebrityInvestments(pb_id, params = {}) {
    await this.delay();
    const celebrity = getCelebrityById(pb_id);
    
    if (!celebrity) {
      return { data: [], total: 0 };
    }
    
    // If celebrity doesn't have full data, generate it
    const fullCelebrity = celebrity.has_full_data ? celebrity : this.generatePartialProfile(celebrity);
    const investments = fullCelebrity.investments || [];
    
    // Format investments to match expected structure
    const formattedInvestments = investments.map((inv, index) => ({
      id: `inv-${pb_id}-${index}`,
      company: {
        name: inv.company_name,
        industry: inv.industry,
        status: inv.status
      },
      roundInfo: {
        date: inv.investment_date,
        announcedDate: inv.investment_date,
        roundType: inv.round_type || 'Unknown',
        amount: inv.initial_investment,
        valuation: inv.current_value || inv.exit_value || inv.initial_investment * 5
      },
      currentValue: inv.current_value,
      exitValue: inv.exit_value,
      multiple: inv.multiple,
      status: inv.status
    }));
    
    // Apply limit if specified
    const limit = params.limit || formattedInvestments.length;
    const limitedData = formattedInvestments.slice(0, limit);
    
    return {
      data: limitedData,
      total: formattedInvestments.length
    };
  }
  
  // Get celebrity exits
  async getCelebrityExits(pb_id) {
    await this.delay();
    const celebrity = getCelebrityById(pb_id);
    
    if (!celebrity || !celebrity.has_full_data) {
      return [];
    }
    
    return celebrity.exits || [];
  }
  
  // Get recent deals
  async getRecentDeals(pb_id) {
    await this.delay();
    const celebrity = getCelebrityById(pb_id);
    
    if (!celebrity || !celebrity.has_full_data) {
      return [];
    }
    
    return celebrity.recent_deals || [];
  }
  
  // Get co-investors
  async getCoInvestors(pb_id) {
    await this.delay();
    const celebrity = getCelebrityById(pb_id);
    
    if (!celebrity || !celebrity.has_full_data) {
      return [];
    }
    
    return celebrity.co_investors || [];
  }
  
  // Get financial metrics
  async getFinancialMetrics(pb_id) {
    await this.delay();
    const celebrity = getCelebrityById(pb_id);
    
    if (!celebrity || !celebrity.has_full_data) {
      return null;
    }
    
    return celebrity.financial_metrics || null;
  }
  
  // Check if celebrity has full data
  async hasFullData(pb_id) {
    const celebrity = getCelebrityById(pb_id);
    return celebrity && celebrity.has_full_data;
  }
  
  // Get activity tiers
  async getActivityTiers() {
    await this.delay();
    return ["Mega Active", "Active", "Moderate", "Emerging", "Inactive"];
  }
  
  // Get celebrity tiers (for compatibility with original service)
  async getCelebrityTiers() {
    await this.delay();
    const celebrities = getAllCelebrities();
    
    // Define the 6 real data celebrities that should appear first
    const realDataCelebrityIds = [
      "105876-55", // Jared Leto (already has real data)
      "162886-33", // Gwyneth Paltrow
      "106502-68", // Will Smith (Willard Smith)
      "230398-75", // Nico Rosberg
      "108080-02", // William Adams
      "106345-45"  // Justin Bieber
    ];
    
    // Separate real data celebrities from others
    const realDataCelebrities = [];
    const otherCelebrities = [];
    
    celebrities.forEach(celebrity => {
      const pbId = celebrity.investor_pb_id || celebrity.pb_id;
      if (realDataCelebrityIds.includes(pbId)) {
        // Place in order specified in realDataCelebrityIds
        const index = realDataCelebrityIds.indexOf(pbId);
        realDataCelebrities[index] = celebrity;
      } else {
        otherCelebrities.push(celebrity);
      }
    });
    
    // Filter out any undefined entries and combine arrays
    const orderedCelebrities = realDataCelebrities.filter(Boolean).concat(otherCelebrities);
    
    // Return as a single flat tier for the grid to display
    return {
      "all": orderedCelebrities
    };
  }
  
  // Compare celebrities
  async compareCelebrities(celebrityIds) {
    await this.delay();
    const celebrities = celebrityIds.map(id => getCelebrityById(id)).filter(Boolean);
    
    if (celebrities.length === 0) {
      throw new Error('No valid celebrities found');
    }
    
    return {
      celebrities: celebrities.map(celeb => ({
        ...celeb,
        comparison_metrics: {
          total_investments: celeb.total_investments,
          total_exits: celeb.total_exits,
          exit_rate: celeb.exit_rate,
          total_invested: celeb.total_invested_active + celeb.total_invested_previous,
          avg_investment_size: Math.floor((celeb.total_invested_active + celeb.total_invested_previous) / celeb.total_investments)
        }
      })),
      summary: {
        highest_investor: celebrities.reduce((a, b) => a.total_investments > b.total_investments ? a : b).investor_name,
        most_exits: celebrities.reduce((a, b) => a.total_exits > b.total_exits ? a : b).investor_name,
        highest_exit_rate: celebrities.reduce((a, b) => a.exit_rate > b.exit_rate ? a : b).investor_name
      }
    };
  }
  
  // Get industry analytics
  async getIndustryAnalytics(industry = null) {
    await this.delay();
    const allCelebrities = getAllCelebrities();
    
    // Calculate industry stats
    const industryStats = {};
    allCelebrities.forEach(celebrity => {
      celebrity.preferred_industries.forEach(ind => {
        if (!industryStats[ind]) {
          industryStats[ind] = {
            industry: ind,
            total_investors: 0,
            total_investments: 0,
            total_invested: 0,
            avg_investment: 0
          };
        }
        industryStats[ind].total_investors++;
        industryStats[ind].total_investments += Math.floor(celebrity.total_investments / celebrity.preferred_industries.length);
        industryStats[ind].total_invested += Math.floor((celebrity.total_invested_active + celebrity.total_invested_previous) / celebrity.preferred_industries.length);
      });
    });
    
    // Calculate averages
    Object.values(industryStats).forEach(stat => {
      stat.avg_investment = Math.floor(stat.total_invested / stat.total_investments);
    });
    
    if (industry) {
      return industryStats[industry] || null;
    }
    
    return Object.values(industryStats).sort((a, b) => b.total_invested - a.total_invested);
  }
  
  // Get platform stats
  async getPlatformStats() {
    await this.delay();
    const celebrities = getAllCelebrities();
    const companies = getAllCompanies();
    
    const totalInvestments = celebrities.reduce((sum, c) => sum + c.total_investments, 0);
    const totalExits = celebrities.reduce((sum, c) => sum + c.total_exits, 0);
    const totalInvested = celebrities.reduce((sum, c) => sum + c.total_invested_active + c.total_invested_previous, 0);
    
    return {
      total_celebrities: celebrities.length,
      total_companies: companies.length,
      total_investments: totalInvestments,
      total_exits: totalExits,
      total_invested: totalInvested,
      avg_exit_rate: (totalExits / totalInvestments * 100).toFixed(1),
      top_industries: this.getTopIndustries(celebrities),
      active_investors: celebrities.filter(c => c.activity_tier === 'Mega Active' || c.activity_tier === 'Active').length
    };
  }
  
  // Helper method for top industries
  getTopIndustries(celebrities) {
    const industryCount = {};
    celebrities.forEach(celebrity => {
      celebrity.preferred_industries.forEach(industry => {
        industryCount[industry] = (industryCount[industry] || 0) + 1;
      });
    });
    
    return Object.entries(industryCount)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([industry, count]) => ({ industry, count }));
  }
  
  // Get industry list
  async getIndustries() {
    await this.delay();
    const allCelebrities = getAllCelebrities();
    const industries = new Set();
    
    allCelebrities.forEach(celebrity => {
      celebrity.preferred_industries.forEach(industry => {
        industries.add(industry);
      });
    });
    
    return Array.from(industries).sort();
  }
  
  // Generate realistic partial profile for celebrities without full data
  generatePartialProfile(celebrity) {
    const totalInvestments = celebrity.total_investments || 10;
    const totalExits = celebrity.total_exits || 0;
    
    // Generate some recent deals based on their investment history
    const recentDeals = this.generateRecentDeals(celebrity, 3);
    
    // Generate a mix of active and exited investments
    const investments = this.generateInvestments(celebrity, Math.min(totalInvestments, 8));
    
    // Generate exits if they have any
    const exits = totalExits > 0 ? this.generateExits(celebrity, Math.min(totalExits, 3)) : [];
    
    // Generate co-investors
    const coInvestors = this.generateCoInvestors(celebrity, 3);
    
    // Generate financial metrics
    const financialMetrics = this.generateFinancialMetrics(celebrity);
    
    return {
      ...celebrity,
      recent_deals: recentDeals,
      investments: investments,
      exits: exits,
      co_investors: coInvestors,
      financial_metrics: financialMetrics,
      has_full_data: true // Fake it to make it look complete
    };
  }
  
  generateRecentDeals(celebrity, count) {
    const companies = [
      "Notion", "Discord", "Figma", "Canva", "Stripe", "Plaid", "Chime", "Brex",
      "Databricks", "Snowflake", "UiPath", "GitLab", "HashiCorp", "Confluent",
      "Datadog", "Monday.com", "Asana", "Slack", "Zoom", "DocuSign"
    ];
    
    const deals = [];
    const usedCompanies = new Set();
    
    for (let i = 0; i < count; i++) {
      let company;
      do {
        company = companies[Math.floor(Math.random() * companies.length)];
      } while (usedCompanies.has(company));
      usedCompanies.add(company);
      
      const date = new Date();
      date.setMonth(date.getMonth() - (i * 2 + 1));
      
      deals.push({
        deal_id: `deal-${celebrity.investor_pb_id}-${i}`,
        company_name: company,
        deal_date: date.toISOString().split('T')[0],
        round_type: ["Series A", "Series B", "Series C", "Series D"][Math.floor(Math.random() * 4)],
        amount_invested: Math.floor(Math.random() * 5000000) + 1000000,
        total_round_size: Math.floor(Math.random() * 100000000) + 20000000,
        post_money_valuation: Math.floor(Math.random() * 1000000000) + 100000000,
        ownership_percentage: (Math.random() * 2 + 0.1).toFixed(2),
        lead_investor: ["Sequoia Capital", "Andreessen Horowitz", "Accel", "Lightspeed"][Math.floor(Math.random() * 4)]
      });
    }
    
    return deals;
  }
  
  generateInvestments(celebrity, count) {
    const companies = [
      "Peloton", "Calm", "Headspace", "Masterclass", "Coursera", "Udacity",
      "DoorDash", "Instacart", "Postmates", "Blue Apron", "HelloFresh",
      "Warby Parker", "Allbirds", "Everlane", "Glossier", "Away"
    ];
    
    const investments = [];
    const usedCompanies = new Set();
    
    for (let i = 0; i < count; i++) {
      let company;
      do {
        company = companies[Math.floor(Math.random() * companies.length)];
      } while (usedCompanies.has(company));
      usedCompanies.add(company);
      
      const initial = Math.floor(Math.random() * 3000000) + 500000;
      const multiple = Math.random() * 8 + 2;
      
      investments.push({
        company_name: company,
        status: i < count / 2 ? "Active" : "Exited",
        initial_investment: initial,
        current_value: Math.floor(initial * multiple),
        exit_value: i >= count / 2 ? Math.floor(initial * multiple) : undefined,
        multiple: multiple.toFixed(1),
        industry: celebrity.preferred_industries[Math.floor(Math.random() * celebrity.preferred_industries.length)],
        investment_date: `20${20 - i}-${String(Math.floor(Math.random() * 12) + 1).padStart(2, '0')}-01`
      });
    }
    
    return investments;
  }
  
  generateExits(celebrity, count) {
    const exits = [];
    
    for (let i = 0; i < count; i++) {
      const initial = Math.floor(Math.random() * 2000000) + 500000;
      const multiple = Math.random() * 15 + 3;
      
      exits.push({
        company_name: `Portfolio Company ${i + 1}`,
        exit_type: ["IPO", "Acquisition", "Secondary"][Math.floor(Math.random() * 3)],
        exit_date: `20${23 - i}-${String(Math.floor(Math.random() * 12) + 1).padStart(2, '0')}-01`,
        initial_investment: initial,
        exit_value: Math.floor(initial * multiple),
        multiple: multiple.toFixed(1),
        holding_period_years: (Math.random() * 5 + 2).toFixed(1),
        acquirer: Math.random() > 0.5 ? ["Google", "Microsoft", "Amazon", "Meta"][Math.floor(Math.random() * 4)] : undefined
      });
    }
    
    return exits;
  }
  
  generateCoInvestors(celebrity, count) {
    const investors = [
      { name: "Marc Andreessen", firm: "Andreessen Horowitz" },
      { name: "Peter Thiel", firm: "Founders Fund" },
      { name: "Reid Hoffman", firm: "Greylock Partners" },
      { name: "Mary Meeker", firm: "Bond Capital" },
      { name: "Bill Gurley", firm: "Benchmark" }
    ];
    
    return investors.slice(0, count).map(investor => ({
      ...investor,
      shared_deals: Math.floor(Math.random() * 10) + 1,
      notable_co_investments: ["Uber", "Airbnb", "Spotify"].slice(0, Math.floor(Math.random() * 3) + 1)
    }));
  }
  
  generateFinancialMetrics(celebrity) {
    const totalInvested = celebrity.total_invested_active + celebrity.total_invested_previous;
    const realizedGains = celebrity.total_invested_previous * 1.8;
    const unrealizedGains = celebrity.total_invested_active * 1.5;
    
    return {
      total_invested: totalInvested,
      total_realized_gains: Math.floor(realizedGains),
      total_unrealized_gains: Math.floor(unrealizedGains),
      gross_multiple: ((realizedGains + unrealizedGains) / totalInvested).toFixed(2),
      irr: (Math.random() * 25 + 15).toFixed(1),
      hit_rate: celebrity.exit_rate || (Math.random() * 30 + 20).toFixed(1),
      average_holding_period: (Math.random() * 3 + 3).toFixed(1)
    };
  }
  
  generateCompanyProfile(company_id) {
    // Extract company name from ID
    const companyName = company_id.replace(/-\d+$/, '')
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    
    const industries = [
      "Enterprise Software", "Consumer Internet", "FinTech", "HealthTech",
      "EdTech", "E-commerce", "AI/ML", "Cybersecurity", "SaaS"
    ];
    
    const founded = 2010 + Math.floor(Math.random() * 10);
    const currentYear = new Date().getFullYear();
    const age = currentYear - founded;
    
    // Generate realistic funding history
    const rounds = [];
    const roundTypes = ["Seed", "Series A", "Series B", "Series C", "Series D"];
    const numRounds = Math.min(Math.floor(age / 2), roundTypes.length);
    
    let lastValuation = 5000000; // Start with $5M
    for (let i = 0; i < numRounds; i++) {
      const amount = lastValuation * (0.2 + Math.random() * 0.3);
      lastValuation = lastValuation * (2 + Math.random() * 1.5);
      
      rounds.push({
        date: `${founded + i * 2}-${String(Math.floor(Math.random() * 12) + 1).padStart(2, '0')}-01`,
        round: roundTypes[i],
        amount: Math.floor(amount),
        valuation: Math.floor(lastValuation)
      });
    }
    
    const latestRound = rounds[rounds.length - 1] || {
      round: "Seed",
      date: `${founded}-06-01`,
      amount: 2000000,
      valuation: 10000000
    };
    
    return {
      company_id,
      name: companyName,
      status: "Active",
      industry: industries[Math.floor(Math.random() * industries.length)],
      founded: founded.toString(),
      celebrity_investors: ["Various celebrity investors"],
      website: `https://${companyName.toLowerCase().replace(/\s/g, '')}.com`,
      description: `${companyName} is a ${age}-year-old company that has raised ${numRounds} rounds of funding. The company operates in the ${industries[0]} space and has shown strong growth potential.`,
      keywords: ["Technology", "Innovation", "Growth"],
      
      latest_funding: {
        round_type: latestRound.round,
        date: latestRound.date,
        amount: latestRound.amount,
        valuation: latestRound.valuation,
        lead_investor: ["Sequoia Capital", "Andreessen Horowitz", "Accel", "Benchmark"][Math.floor(Math.random() * 4)],
        other_investors: ["Sound Ventures", "Various Angels"]
      },
      
      financials: {
        revenue: {
          "2023": Math.floor(lastValuation * 0.1),
          "2024_projected": Math.floor(lastValuation * 0.15)
        },
        arr: Math.floor(lastValuation * 0.15),
        growth_rate: 50 + Math.floor(Math.random() * 100),
        burn_rate: Math.floor(latestRound.amount / 24), // 24 month runway
        runway_months: 24
      },
      
      team: [
        {
          name: "John Doe",
          role: "CEO & Founder",
          background: "Previously at Google/Meta/Amazon"
        },
        {
          name: "Jane Smith",
          role: "CTO & Co-Founder",
          background: "Previously Engineering Lead at Uber"
        }
      ],
      
      deal_history: rounds,
      
      cap_table: {
        founders: 25,
        employees: 15,
        investors: {
          "Lead Investors": 35,
          "Other investors": 25
        }
      },
      
      // Add cap_table_history with N/A values for all generated companies
      cap_table_history: [
        {
          series: "Common Stock",
          shares_authorized: "N/A",
          par_value: "$0.000010",
          dividend_rate: "-",
          original_issue_price: "-",
          liquidation: "-",
          liquidation_multiple: "-",
          conversion_price: "-",
          percent_owned: "25.00"
        },
        {
          series: latestRound.round,
          shares_authorized: "N/A",
          par_value: "$0.000010",
          dividend_rate: "N/A",
          original_issue_price: "N/A",
          liquidation: "N/A",
          liquidation_multiple: "1x",
          conversion_price: "N/A",
          percent_owned: "35.00"
        },
        {
          series: "Employee Pool",
          shares_authorized: "N/A",
          par_value: "$0.000010",
          dividend_rate: "-",
          original_issue_price: "-",
          liquidation: "-",
          liquidation_multiple: "-",
          conversion_price: "-",
          percent_owned: "15.00"
        },
        {
          series: "Other Investors",
          shares_authorized: "N/A",
          par_value: "$0.000010",
          dividend_rate: "N/A",
          original_issue_price: "N/A",
          liquidation: "N/A",
          liquidation_multiple: "1x",
          conversion_price: "N/A",
          percent_owned: "25.00"
        }
      ],
      
      series_terms: {
        series_list: latestRound.round,
        liquidation_preferences: "Pari Passu",
        participating: "Non-participating",
        dividend_rights: "Yes",
        cumulative: "Non-Cumulative",
        anti_dilution: "Weighted Average",
        redemption_rights: "No",
        board_voting_rights: "Yes",
        general_voting_rights: "Yes"
      },
      
      investors: [
        {
          name: "Celebrity Investor",
          type: "Angel (individual)",
          firm: "Independent",
          holding: "Minority",
          since: "N/A",
          board_member: false,
          amount: Math.floor(latestRound.amount * 0.05),
          rounds: [latestRound.round],
          related_deals: "N/A"
        },
        {
          name: latestRound.lead_investor || "Lead Investor VC",
          type: "Venture Capital",
          holding: "Minority",
          since: latestRound.date ? new Date(latestRound.date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : "N/A",
          board_member: true,
          related_deals: `#${numRounds}. ${latestRound.round}: ${latestRound.date}, $${(latestRound.amount / 1000000).toFixed(1)}M, Lead Investor`
        },
        {
          name: "Additional Investor",
          type: "Venture Capital",
          holding: "Minority",
          since: "N/A",
          board_member: false,
          related_deals: "N/A"
        }
      ]
    };
  }
}

export default new MockCelebrityService();